import React from 'react';
import { Card, CardContent, Typography, Box, Skeleton } from '@mui/material';
import { Listing, ListingData } from '../types';

interface SkeletonCardProps {
  listing?: Listing | ListingData;
}

export const SkeletonCard: React.FC<SkeletonCardProps> = ({ listing }) => {
  // Type guard to determine if listing is ListingData or old Listing type
  const isListingData = (
    listing: Listing | ListingData,
  ): listing is ListingData => {
    return 'Property' in listing;
  };

  // Format address
  const getAddress = () => {
    if (!listing) {
      return null;
    }

    if (isListingData(listing)) {
      return listing.Property.UNPARSED_ADDRESS || 'Address not available';
    }

    // Handle old Listing type
    if (listing.UnparsedAddress) {
      return listing.UnparsedAddress;
    }

    const parts = [
      listing.StreetNumber,
      listing.StreetDirPrefix?.value,
      listing.StreetName,
      listing.StreetSuffix?.value,
    ].filter(Boolean);

    const street = parts.join(' ');
    const cityState = [
      listing.City,
      listing.StateOrProvince?.value,
      listing.PostalCode,
    ]
      .filter(Boolean)
      .join(', ');

    return [street, cityState].filter(Boolean).join(', ');
  };

  // Get listing key
  const getListingKey = () => {
    if (!listing) {
      return null;
    }

    if (isListingData(listing)) {
      return listing.Property.LISTING_KEY;
    }
    return listing.ListingKey?.toString() || listing.ListingId;
  };

  return (
    <Card
      sx={{
        height: '100%',
        width: '280px',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'background.secondary',
        border: 1,
        borderRadius: '12px',
        borderColor: 'divider',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          boxShadow: 2,
        },
      }}
    >
      {/* Image container with status badge overlay */}
      <Box sx={{ position: 'relative', mt: 2 }}>
        <Skeleton variant="rectangular" height={200} animation="wave" />
      </Box>

      <CardContent
        sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}
      >
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Skeleton variant="rounded" width={80} height={24} animation="wave" />
        </Box>

        <Box sx={{ mb: 2 }}>
          {listing ? (
            <Typography variant="h6" color="primary.main" gutterBottom>
              {getAddress()}
            </Typography>
          ) : (
            <Skeleton variant="text" width={100} height={24} animation="wave" />
          )}

          <Skeleton
            variant="rounded"
            width="100%"
            height={32}
            animation="wave"
            sx={{ mb: 1 }}
          />

          {listing ? (
            <Typography variant="caption" color="text.primary" display="block">
              MLS#: {getListingKey()}
            </Typography>
          ) : (
            <Skeleton variant="text" width={100} height={24} animation="wave" />
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
          <Skeleton
            variant="rounded"
            width="100%"
            height={32}
            animation="wave"
          />
          <Skeleton variant="rounded" width={40} height={32} animation="wave" />
        </Box>
      </CardContent>
    </Card>
  );
};
